import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  getChecklistTemplates, getChecklistTemplate, createChecklistTemplate, 
  updateChecklistTemplate, deleteChecklistTemplate, seedChecklistTemplates,
  getAllUsers
} from '@/services/api';
import { UserMultiSelect } from '@/components/UserMultiSelect';
import { 
  Plus, FileText, Edit, Trash2, Eye, Copy, 
  ClipboardList, Save, X, Upload, GripVertical,
  AlertTriangle, CheckCircle, Settings
} from 'lucide-react';

// Templates Management Page
export const ChecklistTemplatesPage = () => {
  const { isMasterAdmin } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');

  // New template form state
  const [templateForm, setTemplateForm] = useState({
    name: '',
    checklist_type: 'audit',
    industry: 'logistics',
    description: '',
    scoring_method: 'both',
    items: [],
    default_assignee_ids: []
  });

  useEffect(() => {
    loadTemplates();
  }, [typeFilter]);

  useEffect(() => {
    if (isMasterAdmin) {
      getAllUsers().then(setUsers).catch(() => {});
    }
  }, [isMasterAdmin]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await getChecklistTemplates(
        typeFilter !== 'all' ? typeFilter : null
      );
      setTemplates(data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedTemplates = async () => {
    try {
      await seedChecklistTemplates();
      toast.success('System templates loaded');
      loadTemplates();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCreateTemplate = async () => {
    if (!templateForm.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (templateForm.items.length === 0) {
      toast.error('Add at least one checklist item');
      return;
    }

    try {
      await createChecklistTemplate(templateForm);
      toast.success('Template created');
      setShowCreateDialog(false);
      resetForm();
      loadTemplates();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      await updateChecklistTemplate(selectedTemplate.template_id, templateForm);
      toast.success('Template updated');
      setShowEditDialog(false);
      setSelectedTemplate(null);
      resetForm();
      loadTemplates();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      await deleteChecklistTemplate(templateId);
      toast.success('Template deleted');
      loadTemplates();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setTemplateForm({
      name: template.name,
      checklist_type: template.checklist_type,
      industry: template.industry,
      description: template.description || '',
      scoring_method: template.scoring_method || 'both',
      items: template.items || [],
      default_assignee_ids: (template.default_assignees || []).map(a => a.user_id)
    });
    setShowEditDialog(true);
  };

  const duplicateTemplate = async (template) => {
    setTemplateForm({
      name: `${template.name} (Copy)`,
      checklist_type: template.checklist_type,
      industry: template.industry,
      description: template.description || '',
      scoring_method: template.scoring_method || 'both',
      items: template.items?.map(item => ({
        ...item,
        item_id: '' // Clear IDs to generate new ones
      })) || [],
      default_assignee_ids: (template.default_assignees || []).map(a => a.user_id)
    });
    setShowCreateDialog(true);
  };

  const resetForm = () => {
    setTemplateForm({
      name: '',
      checklist_type: 'audit',
      industry: 'logistics',
      description: '',
      scoring_method: 'both',
      items: [],
      default_assignee_ids: []
    });
  };

  const addItem = () => {
    setTemplateForm({
      ...templateForm,
      items: [
        ...templateForm.items,
        {
          item_id: '',
          text: '',
          category: '',
          is_required: true,
          order: templateForm.items.length,
          response_type: 'checkbox'
        }
      ]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...templateForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setTemplateForm({ ...templateForm, items: newItems });
  };

  const removeItem = (index) => {
    setTemplateForm({
      ...templateForm,
      items: templateForm.items.filter((_, i) => i !== index)
    });
  };

  const moveItem = (index, direction) => {
    const newItems = [...templateForm.items];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;
    
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    newItems.forEach((item, i) => item.order = i);
    setTemplateForm({ ...templateForm, items: newItems });
  };

  if (!isMasterAdmin) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
          <h2 className="text-xl font-semibold">Access Denied</h2>
          <p className="text-muted-foreground">Only the Master Admin can manage checklist templates.</p>
        </div>
      </DashboardLayout>
    );
  }

  const TemplateFormContent = ({ isEdit = false }) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Template Name *</Label>
          <Input
            value={templateForm.name}
            onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
            placeholder="e.g., Warehouse Safety Audit"
            data-testid="template-name-input"
          />
        </div>
        <div>
          <Label>Type</Label>
          <Select
            value={templateForm.checklist_type}
            onValueChange={(v) => setTemplateForm({ ...templateForm, checklist_type: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="audit">Audit</SelectItem>
              <SelectItem value="safety">Safety</SelectItem>
              <SelectItem value="5s">5S</SelectItem>
              <SelectItem value="pick">Pick & Pack</SelectItem>
              <SelectItem value="repack">Repack</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Industry</Label>
          <Select
            value={templateForm.industry}
            onValueChange={(v) => setTemplateForm({ ...templateForm, industry: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="logistics">Logistics</SelectItem>
              <SelectItem value="manufacturing">Manufacturing</SelectItem>
              <SelectItem value="healthcare">Healthcare</SelectItem>
              <SelectItem value="retail">Retail</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Scoring Method</Label>
          <Select
            value={templateForm.scoring_method}
            onValueChange={(v) => setTemplateForm({ ...templateForm, scoring_method: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rating">Rating Only (1-5)</SelectItem>
              <SelectItem value="checklist">Checklist Only</SelectItem>
              <SelectItem value="both">Both Rating & Checklist</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Description</Label>
        <Textarea
          value={templateForm.description}
          onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
          placeholder="Brief description of this checklist template..."
          rows={2}
        />
      </div>

      <div>
        <Label>Default Assignees <span className="text-xs text-muted-foreground font-normal">(auto-assigned when this template is used)</span></Label>
        <UserMultiSelect
          users={users}
          selectedIds={templateForm.default_assignee_ids || []}
          onChange={(ids) => setTemplateForm({ ...templateForm, default_assignee_ids: ids })}
          placeholder="Select default assignees..."
          testIdPrefix="template-default-assignee"
        />
      </div>

      {/* Items Section */}
      <div className="border-t pt-4">
        <div className="flex justify-between items-center mb-4">
          <Label className="text-lg font-semibold">Checklist Items ({templateForm.items.length})</Label>
          <Button type="button" size="sm" onClick={addItem} data-testid="add-item-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Item
          </Button>
        </div>

        {templateForm.items.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
            <ClipboardList className="w-8 h-8 mx-auto text-gray-400 mb-2" />
            <p className="text-muted-foreground">No items yet. Click "Add Item" to start.</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-2">
            {templateForm.items.map((item, index) => (
              <div key={index} className="flex items-start gap-2 p-3 border rounded-lg bg-gray-50">
                <div className="flex flex-col gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => moveItem(index, 'up')}
                    disabled={index === 0}
                  >
                    <span className="text-xs">▲</span>
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => moveItem(index, 'down')}
                    disabled={index === templateForm.items.length - 1}
                  >
                    <span className="text-xs">▼</span>
                  </Button>
                </div>

                <div className="flex-1 space-y-2">
                  <Input
                    value={item.text}
                    onChange={(e) => updateItem(index, 'text', e.target.value)}
                    placeholder="Checklist item text..."
                    className="font-medium"
                  />
                  <div className="flex gap-2">
                    <Input
                      value={item.category}
                      onChange={(e) => updateItem(index, 'category', e.target.value)}
                      placeholder="Category (optional)"
                      className="w-40 text-sm"
                    />
                    <Select
                      value={item.response_type}
                      onValueChange={(v) => updateItem(index, 'response_type', v)}
                    >
                      <SelectTrigger className="w-32 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="checkbox">Checkbox</SelectItem>
                        <SelectItem value="yn">Y/N/P/NA</SelectItem>
                        <SelectItem value="rating">Rating 1-5</SelectItem>
                        <SelectItem value="text">Text Input</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => removeItem(index)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-4 border-t">
        <Button
          onClick={isEdit ? handleUpdateTemplate : handleCreateTemplate}
          className="flex-1"
          data-testid="save-template-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {isEdit ? 'Update Template' : 'Create Template'}
        </Button>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="checklist-templates-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Checklist Templates</h1>
            <p className="text-gray-500">Create and manage checklist templates</p>
          </div>
          <div className="flex gap-3 items-center">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="audit">Audit</SelectItem>
                <SelectItem value="safety">Safety</SelectItem>
                <SelectItem value="5s">5S</SelectItem>
                <SelectItem value="pick">Pick & Pack</SelectItem>
                <SelectItem value="repack">Repack</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleSeedTemplates}>
              <Upload className="w-4 h-4 mr-2" /> Load System Templates
            </Button>
            <Dialog open={showCreateDialog} onOpenChange={(open) => {
              setShowCreateDialog(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="create-template-btn">
                  <Plus className="w-4 h-4 mr-2" /> New Template
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Checklist Template</DialogTitle>
                  <DialogDescription>
                    Build a new checklist template with custom items
                  </DialogDescription>
                </DialogHeader>
                <TemplateFormContent />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{templates.length}</p>
                <p className="text-sm text-muted-foreground">Total Templates</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{templates.filter(t => t.is_system).length}</p>
                <p className="text-sm text-muted-foreground">System</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Settings className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{templates.filter(t => !t.is_system).length}</p>
                <p className="text-sm text-muted-foreground">Custom</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <ClipboardList className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {templates.reduce((sum, t) => sum + (t.items?.length || 0), 0)}
                </p>
                <p className="text-sm text-muted-foreground">Total Items</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Templates List */}
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : templates.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 mb-4">No templates found</p>
              <Button onClick={handleSeedTemplates}>
                <Upload className="w-4 h-4 mr-2" /> Load System Templates
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(template => (
              <Card 
                key={template.template_id} 
                className="hover:shadow-md transition-shadow"
                data-testid={`template-card-${template.template_id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{template.name}</CardTitle>
                      <CardDescription className="line-clamp-2">
                        {template.description || 'No description'}
                      </CardDescription>
                    </div>
                    {template.is_system && (
                      <Badge variant="secondary" className="ml-2">System</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge variant="outline">{template.checklist_type}</Badge>
                    <Badge variant="outline">{template.industry}</Badge>
                    <Badge variant="outline">{template.items?.length || 0} items</Badge>
                    {Array.isArray(template.default_assignees) && template.default_assignees.length > 0 && (
                      <Badge variant="secondary" title={template.default_assignees.map(a => a.name).join(', ')}>
                        {template.default_assignees.length} default assignee{template.default_assignees.length !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => openEditDialog(template)}
                      disabled={template.is_system}
                    >
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => duplicateTemplate(template)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => handleDeleteTemplate(template.template_id)}
                      disabled={template.is_system}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={(open) => {
          setShowEditDialog(open);
          if (!open) {
            setSelectedTemplate(null);
            resetForm();
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Checklist Template</DialogTitle>
              <DialogDescription>
                Modify the template details and items
              </DialogDescription>
            </DialogHeader>
            <TemplateFormContent isEdit />
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ChecklistTemplatesPage;
